import React from 'react';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import handleOpenWhatsApp from 'services/whatsappService';
import StreamingPlayerEmbed from 'components/StreamingPlayerEmbed';

const DEFAULT_TITLE = '¿Por qué TNO Network?';
const DEFAULT_DESCRIPTION =
  'Somos una red multimedia diseñada para que las empresas proyecten su identidad con impacto visual y autoridad editorial. Al integrar TNO Media Lab, nuestro centro de producción avanzada, y TNO Digest, nuestra plataforma editorial, redefinimos la publicidad convencional. Creamos narrativas interactivas, visuales y escritas que generan una conexión real y multicanal con tu audiencia.';

const PILLARS = [
  {
    title: 'Live & Radio Visual (Streaming)',
    text: 'Streaming global en señal en vivo: menciones, segmentos y contexto editorial — no solo un spot aislado.',
    accent: '#1565c0',
    Icon: LiveTvIcon,
  },
  {
    title: 'On-Demand & Media Lab',
    text: 'Producción premium y archivo permanente: tu mensaje sigue trabajando después de la emisión.',
    accent: '#6a1b9a',
    Icon: OndemandVideoIcon,
  },
  {
    title: 'TNO Digest',
    text: 'Autoridad escrita y SEO en tnonetwork.com: contenido que posiciona tu marca en buscadores.',
    accent: '#00838f',
    Icon: MenuBookIcon,
  },
  {
    title: 'Ecosistema 360°',
    text: 'Streaming, redes, web y estudio en un solo plan comercial — conexión real con la audiencia.',
    accent: '#c2185b',
    Icon: DeviceHubIcon,
  },
];

const AUDIENCE_IMAGES = [
  {
    src: '/images/tno-partnership-handshake.jpg',
    alt: 'Acuerdo comercial en estudio TNO Media Lab',
  },
  {
    src: '/images/tno-team-tnonetwork.jpg',
    alt: 'Equipo TNO Network en estudio',
  },
];

function splitLead(description) {
  const text = String(description || DEFAULT_DESCRIPTION).trim();
  const parts = text.split(/\.\s+(?=Al integrar|Creamos)/);
  if (parts.length >= 2) {
    return {
      lead: `${parts[0].trim()}.`,
      body: parts.slice(1).join(' ').trim(),
    };
  }
  const firstSentence = text.match(/^(.+?[.!?])/);
  return {
    lead: firstSentence ? firstSentence[1].trim() : text,
    body: firstSentence ? text.slice(firstSentence[1].length).trim() : '',
  };
}

function PillarCard({ item }) {
  const { title, text, accent, Icon } = item;
  return (
    <Box
      sx={{
        height: '100%',
        p: 2,
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: `1px solid ${alpha(accent, 0.2)}`,
        borderLeft: `4px solid ${accent}`,
        boxShadow: `0 8px 24px ${alpha(accent, 0.08)}`,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.25,
          bgcolor: alpha(accent, 0.12),
          color: accent,
        }}
      >
        <Icon sx={{ fontSize: 24 }} />
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 0.75, lineHeight: 1.3 }}>{title}</Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem', lineHeight: 1.55 }}>{text}</Typography>
    </Box>
  );
}

PillarCard.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    accent: PropTypes.string.isRequired,
    Icon: PropTypes.elementType.isRequired,
  }).isRequired,
};

const Why = ({ content = {} }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const title = content.title || DEFAULT_TITLE;
  const description = content.description || DEFAULT_DESCRIPTION;
  const { lead, body } = splitLead(description);

  return (
    <Box
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        maxWidth: '100%',
        background: `linear-gradient(165deg, ${alpha(primary, 0.06)} 0%, ${alpha(secondary, 0.08)} 48%, transparent 100%)`,
        px: { xs: 1, md: 2 },
        py: { xs: 3, md: 5 },
        my: { xs: 2, md: 3 },
      }}
    >
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} lg={7}>
          <Typography
            sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 2.5, fontSize: '0.78rem' }}
            color="secondary"
            gutterBottom
          >
            Propuesta de valor
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.75rem', md: '2.35rem' },
              letterSpacing: '-0.02em',
              color: 'primary.main',
              mb: 2,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.6, mb: body ? 1.5 : 2.5 }}>
            {lead}
          </Typography>
          {body ? (
            <Typography sx={{ color: 'text.secondary', fontSize: '1rem', lineHeight: 1.7, mb: 3 }}>
              {body}
            </Typography>
          ) : null}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {PILLARS.map((pillar) => (
              <Grid item xs={12} sm={6} key={pillar.title}>
                <PillarCard item={pillar} />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, maxWidth: '100%' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<WhatsAppIcon />}
              onClick={handleOpenWhatsApp}
              sx={{
                borderRadius: 999,
                px: 3,
                fontWeight: 700,
                width: { xs: '100%', sm: 'auto' },
                boxShadow: `0 8px 24px ${alpha(primary, 0.3)}`,
              }}
            >
              Contáctanos
            </Button>
            <Button
              component={RouterLink}
              to="/paquetes"
              variant="outlined"
              color="primary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                borderRadius: 999,
                px: 2.75,
                fontWeight: 700,
                borderWidth: 2,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Ver paquetes
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: 4,
              overflow: 'hidden',
              p: 2,
              background: `linear-gradient(145deg, ${alpha(primary, 0.12)} 0%, ${alpha(secondary, 0.15)} 100%)`,
              border: `1px solid ${alpha(primary, 0.15)}`,
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={7}>
                <Box
                  component="img"
                  src={AUDIENCE_IMAGES[0].src}
                  alt={AUDIENCE_IMAGES[0].alt}
                  sx={{
                    width: '100%',
                    aspectRatio: '1',
                    objectFit: 'cover',
                    borderRadius: 3,
                    boxShadow: `0 12px 32px ${alpha(primary, 0.2)}`,
                    display: 'block',
                  }}
                />
              </Grid>
              <Grid item xs={5}>
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '1',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: `0 10px 28px ${alpha(primary, 0.18)}`,
                    mt: { xs: 0, sm: 3 },
                    position: 'relative',
                    bgcolor: '#000',
                  }}
                >
                  <StreamingPlayerEmbed
                    lazyLoad
                    playLabel="Radio Visual en vivo"
                    sx={{ position: 'absolute', inset: 0 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box
                  component="img"
                  src={AUDIENCE_IMAGES[1].src}
                  alt={AUDIENCE_IMAGES[1].alt}
                  sx={{
                    width: '100%',
                    height: { xs: 160, sm: 200 },
                    objectFit: 'cover',
                    objectPosition: 'center 10%',
                    borderRadius: 3,
                    boxShadow: `0 14px 36px ${alpha(secondary, 0.22)}`,
                    display: 'block',
                  }}
                />
              </Grid>
            </Grid>
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                right: 16,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(primary, 0.12)}`,
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: primary, lineHeight: 1.35 }}>
                Más de una década conectando marcas con audiencias que deciden.
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

Why.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
};

Why.defaultProps = {
  content: {},
};

export default Why;
